import { alpha } from '@mui/material/styles';
import { red } from '@mui/material/colors';

export const brand = {
  50: '#F0F7FF',
  100: '#CEE5FD',
  200: '#9CCCFC',
  300: '#00ffff',
  400: '#0A66C2',
  500: '#55a6f6',
  600: '#064079',
  700: '#157bbf',
  800: '#157bbf',
  900: '#021F3B',
  
  // Pivotes a oscuros
  900: '#0A0E12', // Fondos muy oscuros o contrastes máximos
  1000: '#000000',

  // ----- Efecto 3D Anaglifo -----
  cyan3D: '#54D1FF', // rgb(84, 209, 255)
  red3D: '#FF0004',  // rgb(255, 0, 4)
  green3D: '#0f9b40',  // rgb(15, 155, 64)
  gray3D: '#0A0E12',  // rgb(10, 14, 18)
  white3D: '#FFFFFF',
  
  // Sombra prefabricada lista para usar en los TextFields
  shadow3D: '0px 0px 4px 2px #54d1ff, 1px 1.5px 0px 0px #fff, 4px 4px 4px 0px #ff0004',
  shadowDark: '0px 2px 4px rgba(0, 0, 0, 0.4)',
  shadowRed: '0px 0px 20px 4px #E5383B, 1px 1.5px 0px 0px #fff, 4px 4px 20px 0px #E5383B',
  
  shadowGreen: '0px 0px 20px 4px #0f9b40, 1px 1.5px 0px 0px #fff, 4px 4px 20px 0px #0f9b40'
  
};

export const secondary = {
  50: '#F9F0FF',
  100: '#E9CEFD',
  200: '#D49CFC',
  300: '#B355F6',
  400: '#750AC2',
  500: '#6709AA',
  600: '#490679',
  700: '#3B0363',
  800: '#2F024F',
  900: '#23023B',
};

export const gray = {
  50: '#FBFCFE',
  100: '#EAF0F5',
  200: '#D6E2EB',
  300: '#BFCCD9',
  400: '#94A6B8',
  500: '#5B6B7C',
  600: '#4C5967',
  700: '#364049',
  800: '#131B20',
  900: '#0A0E12',
  1000: '#000000'
};

export const green = {
  50: '#F6FEF6',
  100: '#E3FBE3',
  200: '#C7F7C7',
  300: '#A1E8A1',
  400: '#51BC51',
  500: '#1F7A1F',
  600: '#136C13',
  700: '#0A470A',
  800: '#042F04',
  900: '#021D02',
};

export default function LeidenfrostTheme(mode) {
  return {
    palette: {
      mode,
      divider: mode === 'dark' ? alpha(gray[600], 0.3) : alpha(gray[300], 0.5),
      primary: {
        light: brand[200],
        main: brand[500],
        dark: brand[800],
        contrastText: brand[50],
        ...(mode === 'dark' && {
          contrastText: brand[100],
          light: brand[300],
          main: brand[400],
          dark: brand[800],
        }),
      },
      secondary: {
        light: secondary[400],
        main: secondary[600],
        dark: secondary[900],
        ...(mode === 'dark' && {
          light: secondary[200],
          main: secondary[300],
          dark: secondary[400],
        }),
      },
      warning: {
        main: '#F7B538',
        dark: '#F79F00',
        ...(mode === 'dark' && { main: '#F7B538', dark: '#F79F00' }),
      },
      error: {
        light: red[50],
        main: red[500],
        dark: red[700],
        ...(mode === 'dark' && { 
          light: '#D32F2F', 
          main: '#D32F2F', 
          dark: '#B22A2A' 
        }),
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        ...(mode === 'dark' && {
          light: green[400],
          main: green[500],
          dark: green[700],
        }),
      },
      grey: {
        light: gray[700],
        main: gray[800],
        dark: gray[900],
        ...(mode === 'dark' && {
          light: gray[300],
          main: gray[400],
          dark: gray[500],
        }),
      },
      background: {
        default: gray[800],
        paper: gray[50],
        ...(mode === 'dark' && { default: gray[1000], paper: gray[900] }),
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        ...(mode === 'dark' && { primary: '#fff', secondary: gray[400] }),
      },
      action: {
        selected: `${alpha(brand[200], 0.2)}`,
        ...(mode === 'dark' && {
          selected: alpha(brand[800], 0.2),
        }),
      },
    },
    typography: {
      h1: {
        fontFamily: "'Fjalla One', sans-serif",
        fontSize: 60,
        fontWeight: 1000,
        lineHeight: 78 / 70,
        letterSpacing: 1,
      },
      h2: {
          fontFamily: "'Fjalla One', sans-serif",
        fontSize: 48,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: 2,
      },
      h3: {
          fontFamily: "'Fjalla One', sans-serif",
        fontSize: 42,
        lineHeight: 1.2,
        letterSpacing: 2,
      },
      h4: {
          fontFamily: "'Fjalla One', sans-serif",
        fontSize: 36,
        fontWeight: 800,
        lineHeight: 1.5,
        letterSpacing: 2,
      },
      h5: {
          fontFamily: "'Fjalla One', sans-serif",
        fontSize: 20,
        fontWeight: 600,
        letterSpacing: 2,
      },
      h6: {
          fontFamily: "'Fjalla One', sans-serif",
        fontSize: 18,
        letterSpacing: 2,
      },
      subtitle1: {
        fontSize: 18,
        letterSpacing: 2,
      },
      subtitle2: {
        fontSize: 16,
        letterSpacing: 2,
      },
      body1: {
        fontWeight: 400,
        fontSize: 15,
        letterSpacing: 2,
      },
      body2: {
        fontWeight: 400,
        fontSize: 14,
        letterSpacing: 2,
      },
      caption: {
        fontWeight: 400,
        fontSize: 12,
        letterSpacing: 2,
      },
    },
    components: {
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
          disableGutters: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            padding: 8,
            overflow: 'clip',
            backgroundColor: '#fff',
            border: '0.5px solid',
            borderColor: gray[700],
            ':before': {
              backgroundColor: 'transparent',
            },
            '&:first-of-type': {
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            },
            '&:last-of-type': {
              borderBottomLeftRadius: 10,
              borderBottomRightRadius: 10,
            },
            ...(theme.palette.mode === 'dark' && {
              backgroundColor: gray[900],
              borderColor: gray[800],
            }),
            boxShadow:
                theme.palette.mode === 'light'
                  ? `0 10px 10px 0 rgba(0, 0, 0, 0.1), 0 3x 20px 0 rgba(0, 0, 0, 0.1)`
                  : `0 10px 10px 0 rgba(0, 0, 0, 0.1), 0 3px 20px 0 rgba(0, 0, 0, 0.1)`
          }),
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: 'none',
            borderRadius: 8,
            '&:hover': { backgroundColor: gray[100] },
            ...(theme.palette.mode === 'dark' && {
              '&:hover': { backgroundColor: gray[700] },
            }),
          }),
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: { mb: 20, border: 'none' },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '8px',
            boxShadow: `0 4px 16px ${alpha(gray[700], 0.2)}`,
            '& .Mui-selected': {
              color: brand[500],
            },
            ...(theme.palette.mode === 'dark' && {
              '& .Mui-selected': {
                color: '#fff',
              },
              '&:hover': {
                borderColor: brand['cyan3D'],
              },
              '&.Mui-focused': {
                borderColor: brand['cyan3D'],
              },
            }),
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 500,
            ...(theme.palette.mode === 'dark' && {
              color: gray[400],
              '&.Mui-selected': { 
                color: brand[100] 
              },
            }),
          }),
        },
      },
    
      MuiButton: {
        styleOverrides: {
          root: () => ({
            boxSizing: 'border-box',
            boxShadow: 'none',
            borderRadius: '16px',
            textTransform: 'none',
            '&:active': {
              transform: 'scale(0.98)',
            },
            color: brand[50],
            background: brand["red3D"],
            backgroundImage: `linear-gradient(to bottom, ${brand["red3D"]}, ${brand["red3D"]})`,
            outline: `1px solid E5383B`,
            '&:hover': {
              background: brand["red3D"],
              boxShadow: brand["shadowRed"],
              backgroundImage: 'none',
            },
          }),
        },
        variants: [
          {
          props: { variant: 'text' },
          style: ({ ownerState, theme }) => ({
            color: theme.palette[ownerState.color]?.main || brand[50],
            background: 'none',
            backgroundImage: 'none',
            outline: 'none',
            '&:hover': {
              backgroundColor: theme.palette[ownerState.color] 
                ? alpha(theme.palette[ownerState.color].main, 0.1) 
                : "rgba(50, 50, 50, 0.3)",
              borderColor: "rgba(50, 50, 50, 0.3)",
              background: 'none',
              boxShadow: brand["shadowDark"],
              backgroundImage: 'none',
            },
            // Esto inyecta el color dinámico directamente en la onda del clic
            '& .MuiTouchRipple-child': {
              backgroundColor: theme.palette[ownerState.color]?.main || brand[50],
            },
          })},
          {
            props: { variant: 'outlined' },
            style: {
              color: brand[50],
              background: brand["gray3D"],
              backgroundImage: `linear-gradient(to bottom, ${brand["gray3D"]}, ${brand["gray3D"]})`,
              outline: `1px solid E5383B`,
              '&:hover': {
                background: brand["gray3D"],
                boxShadow: brand["shadowDark"],
                backgroundImage: 'none',
              },
            },
          },
          {
            props: { variant: 'special' },
            style: {
              color: "#ffff !important",
              border: "8px solid #fff",
              padding: "2em 2.5em",
              fontSize: "1em",
              fontWeight: "700",
              fontFamily: "inherit",
              backgroundColor: "transparent",
              borderRadius: "0px",
              cursor: "pointer",
              transition: "backgroundColor 0.25s",
              position: "relative",
              display: "inline-block",
              ":after": {
                position: "absolute",
                marginleft: "0.5em",
                fontWeight: 400
              },
              ":hover::after": {
                  color:"#FFF"
              },
              ':hover': {
                backgroundColor: "#fff",
                color: "#737373 !important",
              }
            },
          },
          {
            props: { variant: 'add' },
            style: ({ ownerState, theme }) => ({
              color: brand[50],
              background: brand['green3D'],
              // 1. Reserva el espacio del borde haciéndolo transparente
              border: '1px solid transparent', 
              // 2. (Opcional) Suaviza la transición para que el efecto 3D se vea premium
              transition: 'border-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
              '&:hover': {
                // Ahora solo cambia el color, evitando el flash blanco
                
                background: brand['green3D'],
                boxShadow: brand['shadowGreen'],
                backgroundImage: 'none',
              },
            }),
          },
        
        ]
      },
      MuiCard: {
        styleOverrides: {
          root: () => ({
            borderRadius: '10px',
            border: `0px solid ${alpha(gray[100], 0.3)}`,
            boxShadow: 'none',
            transition: 'box-shadow 200ms ease-in, border-color 120ms ease-in',
            '&:hover': {
              boxShadow: brand['shadow3D'],
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            alignSelf: 'center',
            py: 1.5,
            px: 0.5,
            background: `linear-gradient(to bottom right, ${brand[50]}, ${brand[100]})`,
            border: '1px solid',
            borderColor: `${alpha(brand[500], 0.3)}`,
            fontWeight: '600',
            '&:hover': {
              backgroundColor: brand[500],
            },
            '&:focus-visible': {
              borderColor: brand[800],
              backgroundColor: brand[200],
            },
            '& .MuiChip-label': {
              color: brand[500],
            },
            '& .MuiChip-icon': {
              color: brand[500],
            },
            ...(theme.palette.mode === 'dark' && {
              background: `linear-gradient(to bottom right, ${brand[700]}, ${brand[900]})`,
              borderColor: `${alpha(brand[500], 0.5)}`,
              '&:hover': {
                backgroundColor: brand[600],
              },
              '&:focus-visible': {
                borderColor: brand[200],
                backgroundColor: brand[600],
              },
              '& .MuiChip-label': {
                color: brand[200],
              },
              '& .MuiChip-icon': {
                color: brand[200],
              },
            }),
          }),
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            marginTop: 16, 
            marginBottom: 16,
            borderColor: `${alpha(gray[200], 0.8)}`,
            ...(theme.palette.mode === 'dark' && {
              borderColor: `${alpha(gray[700], 0.4)}`,
            }),
          }),
        },
      },
      MuiLinearProgress:{
        styleOverrides: {
          root: () => ({
            marginTop: 16, 
            marginBottom: 16,
          }),
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            color: brand[600],
            fontWeight: 500,
            position: 'relative',
            textDecoration: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 0,
              height: '1px',
              bottom: 0,
              left: 0,
              backgroundColor: brand[200],
              opacity: 0.7,
              transition: 'width 0.3s ease, opacity 0.3s ease',
            },
            '&:hover::before': {
              width: '100%',
              opacity: 1,
            },
            ...(theme.palette.mode === 'dark' && {
              color: brand[200],
            }),
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '99px',
            color: gray[500],
            fontWeight: 500,
            ...(theme.palette.mode === 'dark' && {
              color: gray[300],
            }),
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          // Target the actual dialog window, not the invisible wrapper
          paper: ({ theme }) => ({
            // Optional: You can also add your 3D shadows or border-radius here!
            //borderColor: brand['cyan3D'],
            boxShadow: brand['shadow3D'],
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: ({ theme }) => ({
            boxSizing: 'border-box',
            width: 36,
            height: 24,
            padding: 0,
            transition: 'background-color 100ms ease-in',
            '&:hover': {
              '& .MuiSwitch-track': {
                backgroundColor: brand[600],
              },
            },
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                transform: 'translateX(13px)',
              },
            },
            '& .MuiSwitch-track': {
              borderRadius: 50,
            },
            '& .MuiSwitch-thumb': {
              boxShadow: '0 0 2px 2px rgba(0, 0, 0, 0.2)',
              backgroundColor: '#FFF',
              width: 16,
              height: 16,
              margin: 2,
            },
            ...(theme.palette.mode === 'dark' && {
              width: 36,
              height: 24,
              padding: 0,
              transition: 'background-color 100ms ease-in',
              '&:hover': {
                '& .MuiSwitch-track': {
                  backgroundColor: brand[600],
                },
              },
              '& .MuiSwitch-switchBase': {
                '&.Mui-checked': {
                  transform: 'translateX(13px)',
                },
              },
              '& .MuiSwitch-thumb': {
                boxShadow: '0 0 2px 2px rgba(0, 0, 0, 0.2)',
                backgroundColor: '#FFF',
                width: 16,
                height: 16,
                margin: 2,
              },
            }),
          }),
          switchBase: {
            height: 24,
            width: 24,
            padding: 0,
            color: '#fff',
            '&.Mui-checked + .MuiSwitch-track': {
              opacity: 1,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            boxSizing: 'border-box',
            minWidth: 280,
            minHeight: 40,
            height: '100%',
            borderRadius: '10px',
            backgroundColor: alpha(gray[800], 0.4),
            
            // El root vuelve a tener el borde y la sombra base
            border: '1px solid',
            borderColor: gray[600],
            boxShadow: brand['shadowDark'],
            transition: 'border-color 120ms ease-in, box-shadow 120ms ease-in',

            // Efecto Hover
            '&:hover': {
              borderColor: brand['cyan3D'],
            },

            // Efecto Focus (Gafas 3D)
            '&.Mui-focused': {
              borderColor: brand['cyan3D'],
              boxShadow: brand['shadow3D'],
            },

            // APAGAMOS EL FIELDSET POR COMPLETO
            '& fieldset': {
              border: 'none', 
            },
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: gray[600],
            transition: 'color 120ms ease-in',
          },
          select: {
            '&:focus': {
              backgroundColor: 'transparent',
            }
          }
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // Usa el color exacto del fondo de tu Card/Dialog (tu pivote oscuro)
            backgroundColor: '#131B20', 
            padding: '0 6px',     // Le da aire al texto para que no choque con la línea
            marginLeft: '-4px',   // Compensa el padding para que quede alineado a la izquierda
            borderRadius: '4px',  // (Opcional) suaviza el parche
            '&.Mui-focused': {
              color: brand['cyan3D'], // Brilla en cian al hacer clic
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: () => ({
            '& .MuiOutlinedInput-root': {
              boxSizing: 'border-box',
              minWidth: 280,
              minHeight: 40,
              height: '100%',
              borderRadius: '10px',
              border: '1px solid',
              borderColor: gray[600],
              transition: 'border-color 120ms ease-in',
              '& fieldset': {
                border: 'none',
                boxShadow: brand['shadowDark'],
                background: `${alpha(gray[800], 0.4)}`,
              },
              '&:hover': {
                borderColor: brand['cyan3D'],
              },
              '&.Mui-focused': {
                borderColor: brand['cyan3D'],
                boxShadow: brand['shadow3D'],
              },
            },
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root:({theme})=> ({
            width: 300,
            flexShrink: 0,
            backgroundColor: theme.palette.background.default, // contenedor principal
          }),
          paper: ({theme})=> ({
            width: 300,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper, 

            // --- Estilos para el Scrollbar ---
            // Soporte para Firefox
            scrollbarWidth: 'thin', 
            scrollbarColor: `${theme.palette.background.paper} transparent`, 
            
            // Soporte para Chrome, Edge y Safari
            '&::-webkit-scrollbar': {
              width: '8px', // Hace el scrollbar más delgado
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.background.paper, // Gris oscuro para dark theme
              borderRadius: '8px', // Bordes redondeados
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.background.paper, // Color más claro al pasar el cursor
            },
          }),
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: ({theme})=> ({
            backgroundColor: theme.palette.background.paper,
            color: 'white',
            fontWeight: 'bold',
            lineHeight: '48px',
          }),
          sticky: ({theme})=> ({
            backgroundColor: theme.palette.background.paper,
          })
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({theme})=> ({
            backgroundColor: theme.palette.background.paper,
            color: 'white',
            fontWeight: 'bold',
            lineHeight: '48px', 
          }),
          sticky: ({theme})=> ({
            backgroundColor: theme.palette.background.paper,
          })
        },
      },
    },
    mixins: {
      drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px', // Equivalente a theme.spacing(0, 1)
        justifyContent: 'start',
        minHeight: 64, // Debe coincidir con tu toolbar para que todo cuadre
      },
    },
  };
}